import { AsyncPipe } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  input,
  output,
  inject,
  AfterViewInit,
} from '@angular/core';
import {
  ReactiveFormsModule,
  FormsModule,
  UntypedFormGroup,
  UntypedFormBuilder,
} from '@angular/forms';
import { InvoiceService, ToastMessageService } from '@fitness-force/api';
import { AppErrors } from '@fitness-force/models';
// import { FactoryHttpLoader } from '@fitness-force/shared';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { SelectModule } from 'primeng/select';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TimelineModule } from 'primeng/timeline';

import { PdfViewerModule } from 'ng2-pdf-viewer';
import { PopoverModule } from 'primeng/popover';
import type { voidInvoiceRequest } from '@fitness-force/models';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'invoice-details',
  templateUrl: './invoice-details.component.html',
  styleUrls: ['./invoice-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [],
  standalone: true,
  imports: [
    DividerModule,
    TimelineModule,
    TableModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    RippleModule,
    SelectModule,
    AvatarModule,
    TagModule,
    StyleClassModule,
    DialogModule,
    PdfViewerModule,
    PopoverModule,
    TranslateModule,
    AsyncPipe,
  ],
})
export class InvoiceDetailsComponent implements OnInit, AfterViewInit {
  private readonly invoiceService = inject(InvoiceService);
  private readonly translate = inject(TranslateService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly fb = inject(UntypedFormBuilder);

  readonly showInvoicePopup = input<boolean>(false, { alias: 'showInvoice' });
  readonly clientId = input<number>(0);
  readonly tenantId = input<number>(0);
  readonly invoiceId = input<number>(0);
  readonly number_prefix = input<string>('');

  readonly closeInvoice = output<boolean>({ alias: 'closeInvoice' });

  pdfSrc = new BehaviorSubject<string>('');
  showVoidInvoiceModal: boolean = false;
  voidInvoiceForm: UntypedFormGroup = new UntypedFormGroup({});

  ngOnInit(): void {
    this.GetInvoicePdf();
  }

  ngAfterViewInit() {
    this.voidInvoiceForm = this.fb.group({
      void_reason: [''],
    });
  }

  GetInvoicePdf() {
    this.invoiceService
      .getInvoicePDF(this.clientId(), this.invoiceId())
      .subscribe({
        next: (data: any) => {
          this.pdfSrc.next(data);
        },
        error: (error: AppErrors) => {
          this.ShowToastErrors(error);
        },
      });
  }

  printInvoice() {
    window.open(this.pdfSrc.value, '_blank');
  }

  downloadInvoice() {
    const link = document.createElement('a');
    link.setAttribute('target', '_blank');
    link.setAttribute('href', this.pdfSrc.value);
    link.setAttribute('download', `invoice.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  SendInvoicePDF(channel: string) {
    const channels: any[] = [];
    channels.push(channel);
    this.invoiceService
      .SendInvoicePDF(this.clientId(), this.invoiceId(), channels)
      .subscribe({
        next: () => {
          this.translate
            .get('TRANSACTION.MSG.INVOICE_MAIL_MSG')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
        },
        error: (error: AppErrors) => {
          if (error.originalError.status == 200) {
            this.toastMessageService.showSuccess(
              error.originalError.error.text,
            );
          } else {
            this.ShowToastErrors(error);
          }
        },
      });
  }

  deleteInvoice(form: UntypedFormGroup) {
    const voidRequest: voidInvoiceRequest = {} as voidInvoiceRequest;
    voidRequest.void_reason = form.value.void_reason;
    this.invoiceService
      .voidInvoice(
        this.tenantId(),
        this.clientId(),
        this.invoiceId(),
        voidRequest,
      )
      .subscribe({
        next: () => {
          this.translate
            .get('TRANSACTION.MSG.INVOICE_VOID_MSG')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
          this.showVoidInvoiceModal = false;
        },
        error: (error: AppErrors) => {
          this.ShowToastErrors(error);
        },
      });
  }

  closeInvoicePopup() {
    this.closeInvoice.emit(false);
    this.closeInvoice.emit(false);
  }

  ShowToastErrors(error: any) {
    if (error.originalError.error.length > 0) {
      error.originalError.error.forEach((e: any) => {
        this.toastMessageService.showErrorMessage(e.errorMessage);
      });
    } else {
      this.toastMessageService.showErrorMessage(
        error.originalError.error.errorMessage,
      );
    }
  }
}
